<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { ExternalLinkIcon } from '@heroicons/vue/solid';
import { MetaIntegrationService } from '@/services';
import { toast, toastServiceError } from '@/services/core/notification';
import { useUiStore } from '@/stores';
import {
    MetaConnectionStatusResult,
    MetaPhoneNumber,
    MetaWhatsAppNumberPolicy,
    MetaWhatsAppNumberPolicyPhone,
    WhatsAppWabaStatusColorMap,
} from '@/types';
import CompanyPhoneQuotaSummary from './components/CompanyPhoneQuotaSummary.vue';
import WhatsAppMetaHeader from './components/WhatsAppMetaHeader.vue';
import WhatsAppMetaConfirmDialog from './components/WhatsAppMetaConfirmDialog.vue';
import WhatsAppMetaPhoneActivationDialog from './components/WhatsAppMetaPhoneActivationDialog.vue';
import WhatsAppMetaPhoneNumberListItem from './components/WhatsAppMetaPhoneNumberListItem.vue';
import { attendanceUrl } from '@/configs';
import { wabaStatus } from '@/utils/whatsappMeta';

const route = useRoute();
const router = useRouter();
const { locale, t } = useI18n();
const uiStore = useUiStore();
const waba = ref<MetaConnectionStatusResult | null>(null);
const policy = ref<MetaWhatsAppNumberPolicy | null>(null);
const busy = ref(false);
const search = ref('');
const phonePage = ref(1);
const phoneToActivate = ref<MetaPhoneNumber | null>(null);
const isDisconnectOpen = ref(false);
const phoneToDisable = ref<MetaWhatsAppNumberPolicyPhone | null>(null);
const perPage = 10;

const wabaId = computed(() => String(route.params.wabaId));
const metaManagerUrl = computed(
    () =>
        `https://business.facebook.com/latest/whatsapp_manager/phone_numbers/?business_id=${waba.value?.businessId || ''}`,
);
const externalLinks = computed(() =>
    [
        { href: metaManagerUrl.value, label: t('whatsappMeta.detail.manageNumbersInMeta') },
        { href: attendanceUrl, label: t('whatsappMeta.list.openAttendance') },
    ].filter((link) => link.href),
);
const setLoading = (value: boolean) => uiStore.setIsLoading(value);
const digits = (value: string) => value.replace(/\D/g, '');
const emptyPolicy = (): MetaWhatsAppNumberPolicy => ({
    version: 0,
    used: 0,
    limit: null,
    full: false,
    phones: [],
});
const hasMoreThanTenPhones = computed(() => (waba.value?.phoneNumbers.length || 0) > perPage);
const isQuotaAvailable = computed(() => !policy.value?.full || policy.value.limit === null);
const shouldPrepareIntegration = computed(
    () =>
        Boolean(waba.value) &&
        !waba.value?.disconnectedAt &&
        (!waba.value?.systemUserAssigned || !waba.value?.webhookSubscribed),
);

const filteredPhones = computed(() => {
    if (!waba.value) return [];

    const value = search.value.trim().toLocaleLowerCase(locale.value);
    if (!value) return waba.value.phoneNumbers;

    const numeric = digits(value);
    return waba.value.phoneNumbers.filter((phone) =>
        [phone.displayPhoneNumber, phone.verifiedName, phone.phoneNumberId].some(
            (item) =>
                item &&
                (item.toLocaleLowerCase(locale.value).includes(value) ||
                    (numeric && digits(item).includes(numeric))),
        ),
    );
});

const pagePhones = computed(() => {
    if (!hasMoreThanTenPhones.value) return filteredPhones.value;
    return filteredPhones.value.slice((phonePage.value - 1) * perPage, phonePage.value * perPage);
});

const phonePolicy = (phone: MetaPhoneNumber): MetaWhatsAppNumberPolicyPhone | null =>
    policy.value?.phones.find(
        (item) => item.wabaId === wabaId.value && item.phoneNumberId === phone.phoneNumberId,
    ) ?? null;

const status = computed(() => (waba.value ? wabaStatus(waba.value) : null));

const load = async () => {
    setLoading(true);
    try {
        const wabaResponse = await MetaIntegrationService.getWaba(wabaId.value);
        waba.value = wabaResponse.data;
        policy.value = waba.value.disconnectedAt
            ? emptyPolicy()
            : (await MetaIntegrationService.getWhatsAppNumberPolicy()).data;
    } catch (error) {
        toastServiceError(error);
    } finally {
        setLoading(false);
    }
};

const prepareIntegration = async () => {
    if (busy.value) return;

    busy.value = true;
    setLoading(true);
    try {
        waba.value = (await MetaIntegrationService.activateWaba(wabaId.value)).data;
        toast.success(t('whatsappMeta.detail.integrationPrepared'));
        await load();
    } catch (error) {
        toastServiceError(error);
    } finally {
        setLoading(false);
        busy.value = false;
    }
};

const synchronize = async () => {
    if (busy.value) return;

    busy.value = true;
    setLoading(true);
    try {
        waba.value = (await MetaIntegrationService.synchronizeWaba(wabaId.value)).data;
        policy.value = (await MetaIntegrationService.getWhatsAppNumberPolicy()).data;
        toast.success(t('whatsappMeta.detail.numbersSynchronized'));
    } catch (error) {
        toastServiceError(error);
    } finally {
        setLoading(false);
        busy.value = false;
    }
};

const activate = async (pin: string) => {
    if (!phoneToActivate.value?.phoneNumberId || !policy.value || busy.value) return;

    busy.value = true;
    setLoading(true);
    try {
        waba.value = (
            await MetaIntegrationService.activatePhoneNumber(
                wabaId.value,
                phoneToActivate.value.phoneNumberId,
                pin,
                policy.value.version,
            )
        ).data;
        phoneToActivate.value = null;
        await load();
        toast.success(t('whatsappMeta.detail.numberActivated'));
    } catch (error) {
        toastServiceError(error);
        await load();
    } finally {
        setLoading(false);
        busy.value = false;
    }
};

const setEnabled = async (phone: MetaWhatsAppNumberPolicyPhone, enabled: boolean) => {
    if (!policy.value || busy.value) return;

    busy.value = true;
    setLoading(true);
    try {
        await MetaIntegrationService.setWhatsAppPhoneApproval(
            wabaId.value,
            phone.id,
            enabled,
            policy.value.version,
        );
        policy.value = (await MetaIntegrationService.getWhatsAppNumberPolicy()).data;
        toast.success(t(enabled ? 'whatsappMeta.quota.enabled' : 'whatsappMeta.quota.disabled'));
    } catch (error) {
        toastServiceError(error);
        await load();
    } finally {
        setLoading(false);
        busy.value = false;
    }
};

const requestEnabled = (phone: MetaWhatsAppNumberPolicyPhone, enabled: boolean) => {
    if (enabled) return setEnabled(phone, true);
    phoneToDisable.value = phone;
};

const confirmDisable = async () => {
    if (!phoneToDisable.value) return;
    await setEnabled(phoneToDisable.value, false);
    phoneToDisable.value = null;
};

const disconnect = async () => {
    if (busy.value) return;

    busy.value = true;
    setLoading(true);
    try {
        await MetaIntegrationService.disconnectWaba(wabaId.value);
        toast.success(t('whatsappMeta.detail.disconnected'));
        isDisconnectOpen.value = false;
        router.push({ name: 'ListWhatsAppMeta' });
    } catch (error) {
        toastServiceError(error);
    } finally {
        setLoading(false);
        busy.value = false;
    }
};

watch(search, () => (phonePage.value = 1));
onMounted(load);
</script>

<template>
    <div v-if="waba && policy" class="space-y-4 p-4 sm:p-6 lg:p-8">
        <div class="flex flex-wrap items-center justify-between gap-3">
            <UiButton
                variant="white"
                text-variant="primary"
                @click="router.push({ name: 'ListWhatsAppMeta' })"
            >
                {{ t('whatsappMeta.detail.back') }}
            </UiButton>
            <div class="flex flex-wrap gap-3">
                <UiButton
                    variant="white"
                    text-variant="primary"
                    :disabled="busy || Boolean(waba.disconnectedAt)"
                    @click="synchronize"
                >
                    {{ t('whatsappMeta.detail.synchronizeNumbers') }}
                </UiButton>
                <UiButton
                    v-if="shouldPrepareIntegration"
                    variant="primary"
                    text-variant="white"
                    :disabled="busy"
                    @click="prepareIntegration"
                >
                    {{ t('whatsappMeta.detail.prepareIntegration') }}
                </UiButton>
            </div>
        </div>

        <div class="space-y-6 rounded-lg bg-white p-4 shadow sm:p-6">
            <WhatsAppMetaHeader
                :title="waba.displayName || waba.wabaId"
                :subtitle="`WABA ${waba.wabaId}`"
                :status-label="status ? t(`whatsappMeta.list.status.${status}`) : ''"
                :status-variant="status ? WhatsAppWabaStatusColorMap[status] : undefined"
                :summary="t('whatsappMeta.detail.numbersInWaba', waba.phoneNumbers.length)"
            />

            <CompanyPhoneQuotaSummary
                :used="policy.used"
                :limit="policy.limit"
                :full="policy.full"
            />

            <UiSettingsSection
                :title="t('whatsappMeta.detail.numbers')"
                :footer="t('whatsappMeta.detail.numbersFooter')"
            >
                <template #header-action>
                    <UiBadge variant="gray-100" text-variant="gray-700" size="sm">
                        {{ t('whatsappMeta.detail.numberCount', waba.phoneNumbers.length) }}
                    </UiBadge>
                </template>

                <div v-if="hasMoreThanTenPhones" class="px-4 py-2">
                    <UiListSearch
                        v-model="search"
                        :placeholder="t('whatsappMeta.detail.searchNumber')"
                        class="flex-1"
                    />
                </div>

                <UiSettingsRow
                    v-if="waba.phoneNumbers.length === 0"
                    :title="t('whatsappMeta.detail.noNumbersInWaba')"
                    :description="t('whatsappMeta.detail.noNumbersGuidance')"
                />
                <template v-else-if="filteredPhones.length">
                    <WhatsAppMetaPhoneNumberListItem
                        v-for="phone in pagePhones"
                        :key="phone.phoneNumberId"
                        :phone="phone"
                        :policy-phone="phonePolicy(phone)"
                        :can-enable="isQuotaAvailable"
                        @activate="
                            (id) =>
                                (phoneToActivate =
                                    waba?.phoneNumbers.find(
                                        (phone) => phone.phoneNumberId === id,
                                    ) || null)
                        "
                        @approval="requestEnabled"
                    />
                </template>
                <UiSettingsRow v-else :title="t('whatsappMeta.detail.noNumbersFound')" />
            </UiSettingsSection>

            <UiPagination
                v-if="hasMoreThanTenPhones && filteredPhones.length"
                v-model="phonePage"
                :count="filteredPhones.length"
                :per-page="perPage"
            />

            <UiSettingsSection :title="t('whatsappMeta.detail.links')">
                <a
                    v-for="link in externalLinks"
                    :key="link.href"
                    class="flex min-h-[52px] items-center justify-between p-4 text-sm font-medium text-gray-900 hover:bg-gray-50/70"
                    :href="link.href"
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    {{ link.label }}
                    <ExternalLinkIcon class="h-4 w-4 text-gray-400" aria-hidden="true" />
                </a>
            </UiSettingsSection>

            <UiSettingsSection :title="t('whatsappMeta.detail.connection')" variant="danger">
                <UiSettingsRow
                    :title="t('whatsappMeta.detail.disconnectWaba')"
                    :description="t('whatsappMeta.detail.disconnectFootnote')"
                >
                    <template #action>
                        <UiButton
                            variant="danger"
                            text-variant="white"
                            :disabled="busy || Boolean(waba.disconnectedAt)"
                            @click="isDisconnectOpen = true"
                        >
                            {{ t('whatsappMeta.detail.disconnectWaba') }}
                        </UiButton>
                    </template>
                </UiSettingsRow>
            </UiSettingsSection>
        </div>

        <WhatsAppMetaPhoneActivationDialog
            :phone="phoneToActivate"
            :is-submitting="busy"
            @close="phoneToActivate = null"
            @confirm="activate"
        />
        <WhatsAppMetaConfirmDialog
            :open="phoneToDisable !== null"
            :title="t('whatsappMeta.disableDialog.title')"
            :description="
                t('whatsappMeta.disableDialog.description', {
                    phone: phoneToDisable?.displayPhoneNumber || '',
                })
            "
            :confirm-label="t('whatsappMeta.disableDialog.confirm')"
            :is-submitting="busy"
            @close="phoneToDisable = null"
            @confirm="confirmDisable"
        />
        <WhatsAppMetaConfirmDialog
            :open="isDisconnectOpen"
            :title="t('whatsappMeta.disconnectDialog.title')"
            :description="
                t('whatsappMeta.disconnectDialog.description', {
                    waba: waba.displayName || waba.wabaId,
                })
            "
            :confirm-label="t('whatsappMeta.disconnectDialog.confirm')"
            :is-submitting="busy"
            @close="isDisconnectOpen = false"
            @confirm="disconnect"
        />
    </div>
</template>
